// Narrow to types interface from Sentry SDK without requiring @sentry/core typings
type SeverityLevel = 'log' | 'debug' | 'info' | 'warning' | 'error' | 'fatal';
interface Breadcrumb {
  category?: string;
  level?: SeverityLevel;
  data?: any;
  message?: string;
}

import {
  setLevel,
  trace,
  debug,
  info,
  error,
  warn,
  type LogLevelDesc,
} from 'loglevel';
import Sentry from './sentry-logger';

const isReactNative =
  typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

export class Logger {
  static setLevel(level: LogLevelDesc) {
    setLevel(level, false);
  }
  static trace(logDataInput: string | LogData) {
    log(logDataInput, isReactNative ? console.log : trace, 'log');
  }
  static debug(logDataInput: string | LogData) {
    log(logDataInput, isReactNative ? console.log : debug, 'debug');
  }
  static info(logDataInput: string | LogData) {
    log(logDataInput, isReactNative ? console.log : info, 'info');
  }
  static error(logDataInput: string | LogData) {
    log(logDataInput, isReactNative ? console.log : error, 'error');
  }
  static warn(logDataInput: string | LogData) {
    log(logDataInput, isReactNative ? console.warn : warn, 'warning');
  }
}

// Log function
function log(
  logDataInput: string | LogData,
  logger: (LogData) => void,
  severity: SeverityLevel,
) {
  const logData = toLogData(logDataInput);
  logger(logData);

  if (severity === 'error') {
    if (logData?.error) {
      Sentry.captureException(logData?.error, {
        fingerprint: logData.message ? [logData.message] : undefined,
      });
    } else {
      Sentry.captureMessage(logData.message, severity);
    }
  } else {
    Sentry.addBreadcrumb(createSentryBreadCrumb(logData, severity));
  }
}

function toLogData(input: string | LogData): LogData {
  if (typeof input === 'string') {
    return { message: input };
  } else {
    return input;
  }
}

function createSentryBreadCrumb(
  logdata: LogData,
  level: SeverityLevel,
): Breadcrumb {
  return {
    category: 'console',
    level: level,
    data: logdata.extras,
    message: (logdata.context ? `[${logdata.context}] ` : '') + logdata.message,
  };
}

export interface LogData {
  // Optional message. A message is not strictly required
  message?: string;
  extras?: any;
  context?: string;
  // Optional error being logged
  error?: unknown;
}
